import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, type ReactNode } from "react";
import { useSessionContext } from "@/contexts/SessionContext";
import { useBrand } from "@/contexts/BrandContext";
import { sendOTP, verifyOTP, completeProfile, logoutAPI, type SendOTPResponse, type VerifyOTPResponse, type CustomerData, type APIError } from "@/services/authApi";
import { useToast } from "@/hooks/use-toast";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type LoginStep = "phone" | "otp" | "profile";

interface PhoneData {
  country_code: string;
  mobile: string;
  request_id: string;
  otp_length: number;
  is_new_user: boolean;
  masked_number: string;
}

interface CustomerAuthContextValue {
  // State
  isGuest: boolean;
  isAuthenticated: boolean;
  token: string | null;
  customer: CustomerData | null;
  isProfileComplete: boolean;

  // Modal
  loginModalOpen: boolean;
  loginStep: LoginStep;
  phoneData: PhoneData | null;
  showGuestOption: boolean;

  // Actions
  openLogin: (opts?: { onComplete?: () => void; showGuest?: boolean }) => void;
  closeLogin: () => void;
  handleSendOTP: (countryCode: string, mobile: string) => Promise<SendOTPResponse | APIError>;
  handleVerifyOTP: (otp: string) => Promise<VerifyOTPResponse | APIError>;
  handleCompleteProfile: (firstName: string, lastName: string, phoneNumber: string, email?: string) => Promise<boolean>;
  handleSkipProfile: () => void;
  handleLogout: () => Promise<void>;
  goBackToPhone: () => void;

  // Computed
  customerName: string;
  customerMobile: string;
}

const CustomerAuthContext = createContext<CustomerAuthContextValue | undefined>(undefined);

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const { session, loginCustomer, logoutCustomer, isGuest, isAuthenticated } = useSessionContext();
  const { brand } = useBrand();
  const { toast } = useToast();

  // Modal state
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginStep, setLoginStep] = useState<LoginStep>("phone");
  const [phoneData, setPhoneData] = useState<PhoneData | null>(null);
  const [showGuestOption, setShowGuestOption] = useState(false);
  const [onCompleteCallback, setOnCompleteCallback] = useState<(() => void) | null>(null);

  // Customer data from session
  const token = session.customer.token;
  const customer = (session.customer.data as unknown as CustomerData) || null;

  const isProfileComplete = !!(customer?.first_name && customer?.last_name);

  // Auto-reopen profile step if user refreshed mid-profile-completion
  useEffect(() => {
    if (isAuthenticated && token && customer && !customer.first_name) {
      setLoginStep("profile");
      setLoginModalOpen(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openLogin = useCallback((opts?: { onComplete?: () => void; showGuest?: boolean }) => {
    setPhoneData(null);
    setShowGuestOption(opts?.showGuest ?? false);
    if (opts?.onComplete) {
      setOnCompleteCallback(() => opts.onComplete!);
    } else {
      setOnCompleteCallback(null);
    }
    setLoginModalOpen(true);
  }, []);

  const closeLogin = useCallback(() => {
    setLoginModalOpen(false);
    setLoginStep("phone");
    setPhoneData(null);
    setOnCompleteCallback(null);
  }, []);

  const finishLogin = useCallback(() => {
    const name = customer?.first_name;
    // Show personalized greeting if name available, otherwise generic success message
    toast({ 
      title: name ? `مرحباً، ${name}! 👋` : "تم تسجيل الدخول بنجاح ✓" 
    });
    closeLogin();
    if (onCompleteCallback) {
      setTimeout(onCompleteCallback, 100);
    }
  }, [customer, toast, closeLogin, onCompleteCallback]);

  const handleSendOTP = useCallback(async (countryCode: string, mobile: string) => {
    const result = await sendOTP(countryCode, mobile, brand.id);
    if (result.success) {
      setPhoneData({
        country_code: countryCode,
        mobile,
        request_id: result.data.request_id,
        otp_length: result.data.otp_length,
        is_new_user: result.data.is_new_user,
        masked_number: result.data.masked_number,
      });
      setLoginStep("otp");
    }
    return result;
  }, [brand.id]);

  const handleVerifyOTP = useCallback(async (otp: string) => {
    if (!phoneData) {
      return { success: false, error: { code: "NO_PHONE_DATA", message: "Phone data missing" } } as APIError;
    }
    const result = await verifyOTP(phoneData.request_id, phoneData.country_code, phoneData.mobile, otp, brand.id);
    if (result.success) {
      // Update session
      loginCustomer(result.data.token, result.data.customer as unknown as Record<string, unknown>);

      if (result.data.is_new_user) {
        // New user — show ProfileStep to collect name
        setLoginStep("profile");
      } else {
        // Existing user — done
        setTimeout(() => {
          const name = result.data.customer.first_name;
          // Show personalized greeting if name available, otherwise generic success message
          toast({ 
            title: name ? `مرحباً، ${name}! 👋` : "تم تسجيل الدخول بنجاح ✓" 
          });
          closeLogin();
          if (onCompleteCallback) setTimeout(onCompleteCallback, 100);
        }, 100);
      }
    }
    return result;
  }, [phoneData, loginCustomer, toast, closeLogin, onCompleteCallback, brand.id]);

  const handleCompleteProfile = useCallback(async (firstName: string, lastName: string, phoneNumber: string, email?: string) => {
    if (!token) return false;
    const result = await completeProfile(
      token,
      firstName,
      lastName,
      phoneNumber,
      email,
      brand.id,
      phoneData?.country_code,
    );
    if (result.success) {
      loginCustomer(token, result.data.customer as unknown as Record<string, unknown>);
      finishLogin();
      return true;
    }
    return false;
  }, [token, loginCustomer, finishLogin, brand.id, phoneData?.country_code]);

  const handleSkipProfile = useCallback(() => {
    finishLogin();
  }, [finishLogin]);

  const handleLogout = useCallback(async () => {
    if (token) await logoutAPI(token, brand.id);
    logoutCustomer();
    toast({ title: "تم تسجيل الخروج" });
  }, [token, logoutCustomer, toast, brand.id]);

  const goBackToPhone = useCallback(() => {
    setLoginStep("phone");
  }, []);

  const customerName = customer?.first_name || (isGuest ? "زائر" : "");
  const customerMobile = customer?.mobile || "";

  const value = useMemo<CustomerAuthContextValue>(() => ({
    isGuest,
    isAuthenticated,
    token,
    customer,
    isProfileComplete,
    loginModalOpen,
    loginStep,
    phoneData,
    showGuestOption,
    openLogin,
    closeLogin,
    handleSendOTP,
    handleVerifyOTP,
    handleCompleteProfile,
    handleSkipProfile,
    handleLogout,
    goBackToPhone,
    customerName,
    customerMobile,
  }), [
    isGuest, isAuthenticated, token, customer, isProfileComplete,
    loginModalOpen, loginStep, phoneData, showGuestOption,
    openLogin, closeLogin, handleSendOTP, handleVerifyOTP,
    handleCompleteProfile, handleSkipProfile, handleLogout, goBackToPhone,
    customerName, customerMobile,
  ]);

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error("useCustomerAuth must be used within CustomerAuthProvider");
  return ctx;
}
